import React, { useState, useEffect } from 'react';
import { Select, SIZE, TYPE } from 'baseui/select';
import { Button } from 'baseui/button';
import { styled } from 'baseui';
import { Table } from 'baseui/table';
import { Tag } from 'baseui/tag';
import { useContext } from 'react';
import AuthContext from '../shared/AuthContext';

const Container = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '30vh',
  textAlign: 'center',
  width: '100%',
  maxWidth: '960px',
  margin: '0 auto',
  padding: '0 0 24px',
  boxSizing: 'border-box',
});

const SelectContainer = styled('div', {
  margin: '10px',
  width: '100%',
  maxWidth: '420px',
  marginBottom: '20px',
});

const TableContainer = styled('div', {
  margin: '20px auto',
  width: '100%',
  maxWidth: '960px',
  padding: '0 16px',
  boxSizing: 'border-box',
  overflowX: 'auto',
});

const AddException = () => {
  const { apiClient } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedUser, setSelectedUser] = useState([]);
  const [restrictions, setRestrictions] = useState([]);
  const [filterData, setFilterData] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const fetchUsers = async () => {
      try {
        const response = await apiClient.get(`/api/get-users`);
        if (cancelled) return;
        if (response.status === 200) {
          setUsers(response.data);
        } else {
          throw new Error(`Failed to fetch users: ${response.status}`);
        }
      } catch (err) {
        if (!cancelled) console.log(err.message);
      }
    };
    fetchUsers();
    const intervalId = window.setInterval(fetchUsers, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [apiClient]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const response = await apiClient.get(`/api/get-filters`);
        if (response.status === 200) {
          const data = response.data;
          setFilterData(data);
        } else {
          throw new Error(`Failed to fetch filters: ${response.status}`);
        }
      } catch (err) {
        console.log(err.message);
      }
    };
    fetchFilters();
  }, [apiClient]);

  const fetchRestrictions = async () => {
    try {
      const response = await apiClient.get(`/api/get-restrictions`);
      if (response.status === 200) {
        const data = response.data;
        setRestrictions(data);
      } else {
        throw new Error(`Failed to fetch restrictions: ${response.status}`);
      }
    } catch (err) {
      console.log(err.message);
    }
  };

  useEffect(() => {
    fetchRestrictions();
  }, [apiClient]);

  const handleApplyExceptions = async () => {
    try {
      const response = await apiClient.post('/api/add-restrictions', {
        topics: selectedTopics.map(topic => topic.id),
        user: selectedUser.map(user => user.id)
      });
      if (response.status === 200) {
        console.log('Restrictions applied successfully');
        fetchRestrictions(); // Refresh the restrictions table
      } else {
        throw new Error(`Failed to apply restrictions: ${response.status}`);
      }
    } catch (err) {
      console.log(err.message);
    }
  };

  const transformOptions = (options, labelKey, valueKey) => {
    if (!Array.isArray(options) || options.length === 0) {
      return [];
    }
    return options.map((item) => {
      const base = labelKey ? item[labelKey] : item;
      const status = item.online ? ' · Online' : '';
      return {
        id: valueKey ? item[valueKey] : item,
        label: `${base}${status}`,
      };
    });
  };

  return (
    <div className="add-exception-root">
      <Container>
        <h1>Add Exception</h1><br/>
        <SelectContainer>
          <Select
            multi
            labelKey="label"
            valueKey="id"
            size={SIZE.compact}
            value={selectedTopics}
            options={transformOptions(filterData.topic || [], null, null)}
            placeholder="Notebook"
            type={TYPE.select}
            isLoading={false}
            onChange={({ value }) => setSelectedTopics(value)}
          />
        </SelectContainer>
        <SelectContainer>
          <Select
            multi
            labelKey="label"
            valueKey="id"
            size={SIZE.compact}
            value={selectedUser}
            options={transformOptions(users || [], 'name', 'userId')}
            placeholder="Select User"
            type={TYPE.select}
            isLoading={false}
            onChange={({ value }) => setSelectedUser(value)}
          />
        </SelectContainer>
        <Button onClick={handleApplyExceptions}>Apply Exceptions</Button>
      </Container>
      <TableContainer>
        <Table
          columns={['User', 'Topics']}
          data={restrictions.map(restriction => [
            restriction.user.email,
            <div key={restriction.user.email}>
              {(restriction.notebookExcludes || '')
                .split(',')
                .filter(Boolean)
                .map((topic, index) => (
                <Tag key={index} closeable={false}>{topic.trim()}</Tag>
              ))}
            </div>
          ])}
        />
      </TableContainer>
    </div>
  );
};

export default AddException;